import { ReactNode, useEffect, useRef, useState } from "react";

import { useDebouncedSearch } from "@src/hooks/useDebouncedSearch";
import { useSelector } from "@src/store";
import { ExperienceBuilderSelector } from "@src/store/slices/experienceBuilder";

import { NavigationItem, STANDALONE_ITEMS } from "../types";

import { fetchPredictiveOptions } from "./fetchPredictiveOptions";
import { NavigatorContext } from "./NavigatorContext";
import { parseSearchInput } from "./parseSearchInput";
import { isUrlInput } from "./validateNavigableUrl";

/**
 * Navigator data source for a store the logged-in org's admin token can't
 * reach (a configured demo URL): Shopify's token-free public Predictive
 * Search Ajax API. Same input grammar and options shape as the authenticated
 * provider, but capped at 10 results per type with no "load more" — you refine
 * the query instead.
 */
export const UnauthenticatedNavigatorProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const path = useSelector(ExperienceBuilderSelector.getLastPath);
  const origin = useSelector(ExperienceBuilderSelector.getDemoOrigin);
  const [inputValue, setInputValue] = useState("");
  const listboxRef = useRef<HTMLUListElement>(null);

  const debouncedSearch = useDebouncedSearch(inputValue);
  // A URL isn't a search. Suppress when either the live input or the lagging
  // debounced value looks like a URL, so navigating on Enter — which clears the
  // input while the debounce still holds the URL — doesn't fire a stale search.
  const searchQuery =
    isUrlInput(inputValue) || isUrlInput(debouncedSearch)
      ? ""
      : debouncedSearch;
  const parsed = searchQuery ? parseSearchInput(searchQuery) : undefined;
  const searchTerm = parsed?.searchTerm;
  const group = parsed?.group;

  const [options, setOptions] = useState<NavigationItem[]>(STANDALONE_ITEMS);
  const [isLoading, setIsLoading] = useState(false);
  // Set when the demo URL isn't a Shopify storefront (suggest.json 404s), so
  // the Navigator can tell the user search doesn't work here.
  const [searchUnavailable, setSearchUnavailable] = useState(false);

  useEffect(() => {
    // No search → the standalone routes (Home); no fetch needed.
    if (!origin || !searchTerm) {
      setOptions(STANDALONE_ITEMS);
      setSearchUnavailable(false);
      setIsLoading(false);
      return;
    }

    const controller = new AbortController();
    setIsLoading(true);
    fetchPredictiveOptions(origin, searchTerm, group, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setSearchUnavailable(!result.available);
        setOptions(result.available ? result.items : []);
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        console.error("[Navigator] Predictive search failed:", error);
        // A thrown fetch is also "we never got an answer" — same as a non-JSON
        // response. Leaving the flag at its previous value stranded a stale
        // "search isn't available" on a store that had since been changed.
        setSearchUnavailable(true);
        setOptions([]);
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });

    return () => controller.abort();
  }, [origin, searchTerm, group]);

  const selectedOption = options.find((opt) => opt.path === path) ?? null;

  return (
    <NavigatorContext.Provider
      value={{
        inputValue,
        setInputValue,
        options,
        selectedOption,
        isLoading,
        loadingGroup: undefined,
        // Public Predictive Search has no cursors, so there's nothing to page.
        showLoadMore: () => false,
        onLoadMore: () => {},
        listboxRef,
        noOptionsText: searchUnavailable
          ? "Search isn't available for this demo URL"
          : undefined,
      }}
    >
      {children}
    </NavigatorContext.Provider>
  );
};
